import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, IconButton, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../hooks/usePermission';
import { UserType } from '../../interfaces/userType.enum';
import { RotationPattern } from '../../interfaces/shift/RotationPattern';
import { Shift } from '../../interfaces/shift/Shift';
import { RotationPatternService } from '../../services/rotation-pattern.service';
import { ShiftService } from '../../services/shift.service';
import RotationPatternEditDialog from './RotationPatternEditDialog';

// NOTE: No Edit button is shown because the backend has no update endpoint for rotation patterns.
// Users must delete and re-create a pattern to make changes.
// When a backend PATCH/PUT endpoint is added, restore the Edit IconButton and wire it here.

const RotationPatternListPage: React.FC = () => {
    const { user, selectedInstitutionId } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);

    const isInstitutionAdmin = user?.roles.includes(UserType.InstitutionAdmin) ?? false;
    const canRotate = usePermission('SHIFT_ROTATE', isInstitutionAdmin);

    const [patterns, setPatterns] = useState<RotationPattern[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        if (selectedInstitutionId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId]);

    const fetchData = async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const [patternData, shiftData] = await Promise.all([
                RotationPatternService.list(selectedInstitutionId),
                ShiftService.list(selectedInstitutionId),
            ]);
            setPatterns(patternData);
            setShifts(shiftData);
        } catch (err) {
            toast.showApiError(err, t('patternList.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setDialogOpen(true);
    };

    const handleDelete = async (pattern: RotationPattern) => {
        if (!selectedInstitutionId) return;
        if (!window.confirm(t('patternList.confirmDelete', { name: pattern.name }))) return;
        try {
            await RotationPatternService.remove(selectedInstitutionId, pattern.id);
            toast.showSuccess(t('patternList.deleted'));
            await fetchData();
        } catch (err) {
            toast.showApiError(err, t('patternList.deleteFailed'));
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleSaved = () => {
        fetchData();
    };

    if (loading) return <Typography sx={{ p: 3 }}>{t('loading')}</Typography>;

    return (
        <Box sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('patternList.title')}</Typography>
                {canRotate && (
                    <Button variant="contained" color="primary" onClick={handleCreate}>
                        {t('patternList.newPattern')}
                    </Button>
                )}
            </Box>

            {patterns.length === 0 ? (
                <Typography>{t('patternList.empty')}</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('common:fields.name')}</TableCell>
                                <TableCell>{t('patternList.columns.daysPerStep')}</TableCell>
                                <TableCell>{t('patternList.columns.startDate')}</TableCell>
                                <TableCell>{t('columns.active')}</TableCell>
                                <TableCell>{t('patternList.columns.steps')}</TableCell>
                                {canRotate && <TableCell>{t('common:fields.actions')}</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {patterns.map(pattern => (
                                <TableRow key={pattern.id}>
                                    <TableCell>{pattern.name}</TableCell>
                                    <TableCell>{pattern.daysPerStep}</TableCell>
                                    <TableCell>{pattern.startDate}</TableCell>
                                    <TableCell>
                                        {pattern.isActive
                                            ? <Chip label={t('common:status.active')} color="success" size="small" />
                                            : <Chip label={t('common:status.inactive')} size="small" />}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={t('patternList.shiftCount', { count: pattern.shiftIdsInOrder.length })}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    {canRotate && (
                                        <TableCell>
                                            <IconButton
                                                onClick={() => handleDelete(pattern)}
                                                color="error"
                                                aria-label={t('ariaDelete')}
                                                size="small"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedInstitutionId && (
                <RotationPatternEditDialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    institutionId={selectedInstitutionId}
                    shifts={shifts}
                    onSaved={handleSaved}
                />
            )}
        </Box>
    );
};

export default RotationPatternListPage;
